import React, { useCallback, useEffect, useState } from "react";
import * as Yup from "yup";
import { Formik, Form } from "formik";

import {
  Button,
  Flex,
  FormHeading,
  Grid,
  Label,
  Text,
  Box,
  ErrorMsg,
  Toastify,
} from "atoms";
import { FormCheckbox, FormInput, FormSelect } from "molecules/FormInput";
import MemoClose from "assets/icons/Close";
import MemoAdd from "assets/icons/Add";
import { optionFetch } from "utils/utilities";
import { usePropsoch } from "contexts/Propsoch";
import { useParams } from "react-router-dom";

const defInfraObj = {
  nearbyInfraValues: [
    {
      nearbyInfraValueId: "",
      images: [],
      distanceToProject: "",
    },
  ],
};
const defSubInfraObj = {
  nearbyInfraValueId: "",
  images: [],
  distanceToProject: "",
};

export const SocialInfrastructureForm = ({
  onNext,
  onPrevious,
  bindHitOnSave,
  fetchProperty,
  rootProperty,
}) => {
  const {
    actions: {
      getPropertyListingSocialInfra,
      createPropertyListingSocialInfra,
      updatePropertyListingSocialInfra,
    },
  } = usePropsoch();

  const { id } = useParams();

  // fetch property
  const [property, setProperty] = useState(null);

  useEffect(() => {
    id &&
      getPropertyListingSocialInfra(id)?.then((data) => {
        setProperty(data);
        const chosenInfrastructure = [];
        let chosenInfrastructureData = {};
        data?.infrastructure?.forEach((infra) => {
          // set infra
          chosenInfrastructure?.push(infra?.nearbyInfraId);

          // set infra data
          chosenInfrastructureData[infra?.nearbyInfraId?.toString()] = {};
          chosenInfrastructureData[
            infra?.nearbyInfraId?.toString()
          ].nearbyInfraValues = infra?.nearbyInfraValues?.map(
            (nearbyInfraValue) => ({
              nearbyInfraValueId: {
                value: nearbyInfraValue?.nearbyInfraValue?.nearbyInfraValueId,
                label: nearbyInfraValue?.nearbyInfraValueName,
              },
              // images: nearbyInfraValue?.images,
              distanceToProject:
                nearbyInfraValue?.nearbyInfraValue?.distanceToProject,
            })
          );
        });

        // reformat type
        data &&
          setProperty((prev) => ({
            ...prev,
            // infra
            chosenInfrastructure: chosenInfrastructure,
            // infra data
            chosenInfrastructureData: chosenInfrastructureData,
          }));
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // submit
  const [submitLoading, setSubmitLoading] = useState(false);
  const onSubmit = useCallback(
    ({ chosenInfrastructure, chosenInfrastructureData, dirty }) => {
      setSubmitLoading(true);
      if (dirty) {
        // new nearbyAreas total
        const infrastructure = chosenInfrastructure?.map((item) => ({
          nearbyInfraId: item,
          nearbyInfraValues:
            chosenInfrastructureData?.[
              item?.toString()
            ]?.nearbyInfraValues?.map((nearbyInfraValue) => ({
              nearbyInfraValueId: nearbyInfraValue?.nearbyInfraValueId?.value,
              distanceToProject: nearbyInfraValue?.distanceToProject,
              // images: nearbyInfraValue?.images,
            })) || [],
        }));

        const formattedValues = {
          propertyId: id,
          infrastructure,
        };

        if (property) {
          updatePropertyListingSocialInfra(
            { ...formattedValues },
            id,
            rootProperty?.name
          )
            .then(() => {
              Toastify("success", "Social infrastructure updated");
              fetchProperty()?.then(() => {
                onNext();
              });
            })
            .catch((e) => {
              Toastify("error", "Social infrastructure not updated");
            })
            .finally(() => {
              setSubmitLoading(false);
            });
        } else {
          createPropertyListingSocialInfra(formattedValues, rootProperty?.name)
            .then(() => {
              Toastify("success", "Social infrastructure created");
              fetchProperty()?.then(() => {
                onNext();
              });
            })
            .catch((e) => {
              Toastify("error", "Social infrastructure not created");
            })
            .finally(() => {
              setSubmitLoading(false);
            });
        }
      } else {
        if (id) {
          onNext();
        }
      }
    },
    [
      createPropertyListingSocialInfra,
      id,
      updatePropertyListingSocialInfra,
      onNext,
      property,
      rootProperty,
      fetchProperty,
    ]
  );
  // nearby infrastructure, infrastructures
  const [nearbyInfrastructure, setNearbyInfrastructure] = useState([]);
  useEffect(() => {
    // nearby area
    optionFetch("/nearbyinfras/get", "name", "id").then((data) => {
      setNearbyInfrastructure(data);
    });
  }, []);

  const validationSchema = Yup.object().shape({
    chosenInfrastructure: Yup.array()
      .min(1, "Choose a infrastructure")
      .typeError("Infrastructure is required")
      .required("Infrastructure is required"),
    chosenInfrastructureData: Yup.lazy((v) => {
      const inKeysSchemaObjectShape = Object.keys(v || {})?.reduce(
        (pre, cur) => ({
          ...pre,
          [cur?.toString()]: Yup.object().shape({
            nearbyInfraValues: Yup.array()
              .of(
                Yup.object().shape({
                  nearbyInfraValueId: Yup.object().shape({
                    label: Yup.string().required(
                      `Choose a ${
                        nearbyInfrastructure?.find(
                          (i) => i?.value === parseInt(cur || 0)
                        )?.label
                      }`
                    ),
                  }),
                  distanceToProject: Yup.number()
                    .min(0, "Value should be greater than 0")
                    .typeError("Invalid number")
                    .required("Distance is required"),
                })
              )
              .min(
                1,
                `Choose a ${
                  nearbyInfrastructure?.find(
                    (i) => i?.value === parseInt(cur || 0)
                  )?.label
                }`
              ),
          }),
        }),
        {}
      );

      return Yup.object().shape({ ...inKeysSchemaObjectShape });
    }),
  });

  return (
    <Formik
      initialValues={{
        chosenInfrastructure: [],
        chosenInfrastructureData: {},
        ...property,
      }}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
      enableReinitialize
    >
      {({ values, setFieldValue, submitForm, dirty, touched, errors }) => {
        bindHitOnSave(() => {
          setFieldValue("dirty", dirty);
          submitForm();
        });
        return (
          <Form>
            <Grid gridGap="2.4rem">
              <FormHeading text="Social Infrastructure" />
              <Label text="Nearby areas Infrastructures" req />
              <Grid
                // gridAutoFlow="column"
                gridTemplateColumns="repeat(5,1fr)"
                gridGap="3rem"
                justifyContent="start"
                mb="1rem"
              >
                {nearbyInfrastructure?.map((item, index) => (
                  <Flex aI="center" key={index}>
                    <FormCheckbox
                      name="chosenInfrastructure"
                      height="1.8rem"
                      value={item?.value}
                      onChange={(isChecked) => {
                        if (isChecked) {
                          setFieldValue(
                            `chosenInfrastructureData.${item?.value?.toString()}`,
                            defInfraObj
                          );
                        } else {
                          setFieldValue(
                            `chosenInfrastructureData.${item?.value?.toString()}`,
                            undefined
                          );
                        }
                      }}
                    />
                    <Text color="neutral.700">{item?.label}</Text>
                  </Flex>
                ))}
              </Grid>
              {touched?.chosenInfrastructure &&
                errors?.chosenInfrastructure && (
                  <ErrorMsg text={errors?.chosenInfrastructure} />
                )}
              {Array.isArray(values?.chosenInfrastructure) &&
                values?.chosenInfrastructure?.map(
                  (currentInfrastructure, curIndex) => (
                    <Grid
                      border="1px solid"
                      borderColor="neutral.200"
                      p="2.4rem"
                      gridRowGap="2.4rem"
                      borderRadius="1rem"
                      key={curIndex}
                    >
                      {values?.chosenInfrastructureData?.[
                        currentInfrastructure?.toString()
                      ]?.nearbyInfraValues?.map((subInfra, subIndex) => (
                        <Box key={subIndex}>
                          {/* <Flex jC="space-between">
                            <Label
                              text={`${
                                nearbyInfrastructure?.find(
                                  (i) => i?.value === currentInfrastructure
                                )?.label
                              } ${subIndex + 1}`}
                              mb="1.6rem"
                              {...(!subIndex && { req: true })}
                            />
                            {subIndex > 0 && (
                              <Flex
                                alignItems="center"
                                color="accents.100"
                                cursor="pointer"
                                onClick={() => {
                                  const duplicate = JSON.parse(
                                    JSON.stringify(
                                      values?.chosenInfrastructureData?.[
                                        currentInfrastructure?.toString()
                                      ]?.nearbyInfraValues
                                    )
                                  );
                                  setFieldValue(
                                    `chosenInfrastructureData.[${currentInfrastructure?.toString()}].nearbyInfraValues`,
                                    [
                                      ...duplicate.slice(0, subIndex),
                                      ...duplicate.slice(subIndex + 1),
                                    ]
                                  );
                                }}
                              >
                                <MemoClose height="1.3rem" width="1.3rem" />
                                <Text
                                  color="accents.100"
                                  fontFamily="NeometricBold"
                                  ml="1rem"
                                >
                                  Remove
                                </Text>
                              </Flex>
                            )}
                          </Flex>
                          <Grid
                            gridTemplateColumns="1fr 1fr"
                            gridGap="2.4rem"
                            pb="2.4rem"
                          >
                            <FormSelect
                              name={`chosenInfrastructureData.[${currentInfrastructure?.toString()}].nearbyInfraValues.[${subIndex}].nearbyInfraValueId`}
                              placeholder="Select drop down"
                              loadOptions={(inputString) =>
                                optionFetch(
                                  `/nearbyinfravalues/get/type?type=${
                                    nearbyInfrastructure?.find(
                                      (i) => i?.value === currentInfrastructure
                                    )?.label
                                  }`,
                                  "name",
                                  "id",
                                  inputString,
                                  (res) => {
                                    return res?.filter(
                                      (resItem) =>
                                        !values?.chosenInfrastructureData?.[
                                          currentInfrastructure?.toString()
                                        ]?.nearbyInfraValues?.find(
                                          (nearbyInfraValue) =>
                                            nearbyInfraValue?.nearbyInfraValueId
                                              ?.value === resItem?.value
                                        )
                                    );
                                  }
                                )
                              }
                              filterOption={(option, inputValue) => {
                                return (
                                  !values?.chosenInfrastructureData?.[
                                    currentInfrastructure?.toString()
                                  ]?.nearbyInfraValues?.find(
                                    (nearbyInfraValue) =>
                                      nearbyInfraValue?.nearbyInfraValueId
                                        ?.value === option?.value
                                  ) &&
                                  option?.label
                                    ?.toLowerCase()
                                    ?.includes(
                                      (inputValue || "")?.toLowerCase()
                                    )
                                );
                              }}
                              isOptionLoadAsync
                              defaultOptions
                              cacheOptions
                            />
                          </Grid> */}
                          <Grid
                            gridTemplateColumns="1fr 1fr"
                            gridGap="2.4rem"
                            pb="2.4rem"
                          >
                            <Label
                              text={`${
                                nearbyInfrastructure?.find(
                                  (i) => i?.value === currentInfrastructure
                                )?.label
                              } ${subIndex + 1}`}
                              // mb="1.6rem"
                              {...(!subIndex && { req: true })}
                            />
                            <Flex jC="space-between">
                              <Label
                                text={`Distance to Project (in kms)`}
                                {...(!subIndex && { req: true })}
                                // mb="1.6rem"
                              />
                              {subIndex > 0 && (
                                <Flex
                                  alignItems="center"
                                  color="accents.100"
                                  cursor="pointer"
                                  onClick={() => {
                                    const duplicate = JSON.parse(
                                      JSON.stringify(
                                        values?.chosenInfrastructureData?.[
                                          currentInfrastructure?.toString()
                                        ]?.nearbyInfraValues
                                      )
                                    );
                                    setFieldValue(
                                      `chosenInfrastructureData.[${currentInfrastructure?.toString()}].nearbyInfraValues`,
                                      [
                                        ...duplicate.slice(0, subIndex),
                                        ...duplicate.slice(subIndex + 1),
                                      ]
                                    );
                                  }}
                                >
                                  <MemoClose height="1.3rem" width="1.3rem" />
                                  {/* <Text
                                  color="accents.100"
                                  fontFamily="NeometricBold"
                                  ml="1rem"
                                >
                                  Remove
                                </Text> */}
                                </Flex>
                              )}
                            </Flex>

                            {/* chosenInfrastructureData -> currentInfrastructure?.toString() -> { nearbyInfraValues:  [ {id ,images} ] } */}
                            <FormSelect
                              name={`chosenInfrastructureData.[${currentInfrastructure?.toString()}].nearbyInfraValues.[${subIndex}].nearbyInfraValueId`}
                              placeholder="Select drop down"
                              loadOptions={(inputString) =>
                                optionFetch(
                                  `/nearbyinfravalues/get/type?type=${
                                    nearbyInfrastructure?.find(
                                      (i) => i?.value === currentInfrastructure
                                    )?.label
                                  }`,
                                  "name",
                                  "id",
                                  inputString,
                                  (res) => {
                                    return res?.filter(
                                      (resItem) =>
                                        !values?.chosenInfrastructureData?.[
                                          currentInfrastructure?.toString()
                                        ]?.nearbyInfraValues?.find(
                                          (nearbyInfraValue) =>
                                            nearbyInfraValue?.nearbyInfraValueId
                                              ?.value === resItem?.value
                                        )
                                    );
                                  }
                                )
                              }
                              filterOption={(option, inputValue) => {
                                return (
                                  !values?.chosenInfrastructureData?.[
                                    currentInfrastructure?.toString()
                                  ]?.nearbyInfraValues?.find(
                                    (nearbyInfraValue) =>
                                      nearbyInfraValue?.nearbyInfraValueId
                                        ?.value === option?.value
                                  ) &&
                                  option?.label
                                    ?.toLowerCase()
                                    ?.includes(
                                      (inputValue || "")?.toLowerCase()
                                    )
                                );
                              }}
                              isOptionLoadAsync
                              defaultOptions
                              cacheOptions
                            />
                            <FormInput
                              name={`chosenInfrastructureData.[${currentInfrastructure?.toString()}].nearbyInfraValues.[${subIndex}].distanceToProject`}
                              // type="number"
                              placeholder="Enter in kms"
                              req
                            />
                          </Grid>
                          {/* <FileUpload
                            files={
                              values?.chosenInfrastructureData?.[
                                currentInfrastructure?.toString()
                              ]?.nearbyInfraValues?.[subIndex]?.images
                            }
                            onUpload={(locations) => {
                              const newProjectImages = [
                                ...(values?.chosenInfrastructureData?.[
                                  currentInfrastructure?.toString()
                                ]?.nearbyInfraValues?.[subIndex]?.images
                                  ? JSON.parse(
                                      JSON.stringify(
                                        values?.chosenInfrastructureData?.[
                                          currentInfrastructure?.toString()
                                        ]?.nearbyInfraValues?.[subIndex]?.images
                                      )
                                    ) || []
                                  : []),
                                ...locations,
                              ];

                              setFieldValue(
                                `chosenInfrastructureData.[${currentInfrastructure?.toString()}].nearbyInfraValues.[${subIndex}].images`,
                                newProjectImages
                              );
                            }}
                            onCross={(index) => {
                              const currProjectImages = [
                                ...(values?.chosenInfrastructureData?.[
                                  currentInfrastructure?.toString()
                                ]?.nearbyInfraValues?.[subIndex]?.images
                                  ? JSON.parse(
                                      JSON.stringify(
                                        values?.chosenInfrastructureData?.[
                                          currentInfrastructure?.toString()
                                        ]?.nearbyInfraValues?.[subIndex]?.images
                                      )
                                    ) || []
                                  : []),
                              ];
                              const total = currProjectImages?.length;

                              const newProjectImages = [
                                ...currProjectImages?.slice(0, index),
                                ...currProjectImages?.slice(index + 1, total),
                              ];

                              setFieldValue(
                                `chosenInfrastructureData.[${currentInfrastructure?.toString()}].nearbyInfraValues.[${subIndex}].images`,
                                newProjectImages
                              );
                            }}
                          /> */}
                        </Box>
                      ))}

                      <Flex
                        alignItems="center"
                        color="secondary.700"
                        cursor="pointer"
                        onClick={() => {
                          const duplicateObj = JSON.parse(
                            JSON.stringify(
                              values?.chosenInfrastructureData?.[
                                currentInfrastructure?.toString()
                              ]
                            )
                          );

                          setFieldValue(
                            `chosenInfrastructureData.${currentInfrastructure?.toString()}.nearbyInfraValues`,
                            [...duplicateObj?.nearbyInfraValues, defSubInfraObj]
                          );
                        }}
                      >
                        <MemoAdd height="1.3rem" width="1.3rem" />
                        <Text
                          color="secondary.700"
                          fontFamily="NeometricBold"
                          ml="1rem"
                        >
                          Add{" "}
                          {nearbyInfrastructure?.find(
                            (i) => i?.value === currentInfrastructure
                          )?.label || ""}
                        </Text>
                      </Flex>
                    </Grid>
                  )
                )}

              <Flex my="2rem" gridGap="1.6rem">
                <Button
                  px="3rem"
                  width="fit-content"
                  variant="primaryOutline"
                  height="4.8rem"
                  onClick={onPrevious}
                >
                  Previous
                </Button>
                <Button
                  px="3rem"
                  width="fit-content"
                  variant="primary"
                  height="4.8rem"
                  type="submit"
                  loading={submitLoading}
                  onClick={() => {
                    setFieldValue("dirty", dirty);
                    submitForm();
                  }}
                >
                  Next
                </Button>
              </Flex>
            </Grid>
          </Form>
        );
      }}
    </Formik>
  );
};
